import type { ReactNode } from 'react';

import { Button } from '@/components/ui/Button';
import { Icon, type IconName } from '@/components/ui/Icon';
import { SkeletonList } from '@/components/ui/Skeleton';
import { cn } from '@/utils/cn';
import { getErrorMessage } from '@/utils/errors';

import styles from './StateMessage.module.css';

interface StateLayoutProps {
  compact?: boolean | undefined;
  className?: string | undefined;
}

export interface EmptyStateProps extends StateLayoutProps {
  icon?: IconName;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}

/** Nothing to show yet — say why and offer the next step. */
export function EmptyState({
  icon = 'info',
  title,
  description,
  action,
  compact,
  className,
}: EmptyStateProps) {
  return (
    <div className={cn(styles.state, compact && styles.compact, className)}>
      <span className={styles.iconWrap}>
        <Icon name={icon} size={compact ? 20 : 24} />
      </span>
      <p className={styles.title}>{title}</p>
      {description !== undefined && <p className={styles.description}>{description}</p>}
      {action}
    </div>
  );
}

export interface ErrorStateProps extends StateLayoutProps {
  error: unknown;
  title?: string;
  onRetry?: () => void;
}

/** A failed load, with the reason and a retry. */
export function ErrorState({
  error,
  title = 'This could not be loaded',
  onRetry,
  compact,
  className,
}: ErrorStateProps) {
  return (
    <div role="alert" className={cn(styles.state, compact && styles.compact, className)}>
      <span className={cn(styles.iconWrap, styles.errorIcon)}>
        <Icon name="alert" size={compact ? 20 : 24} />
      </span>
      <p className={styles.title}>{title}</p>
      <p className={styles.description}>{getErrorMessage(error)}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" icon="refresh" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export interface LoadingStateProps extends StateLayoutProps {
  label?: string;
}

/** Grey placeholder rows in the shape of a list, instead of a spinner, while data loads. */
export function LoadingState({ label = 'Loading…', compact, className }: LoadingStateProps) {
  return <SkeletonList rows={compact ? 3 : 5} label={label} className={className} />;
}
